import React, { useEffect, useRef, useState } from 'react';
import { fetchDashboardRankHistory } from '../dashboardRankHistoryApi';
import { fetchDashboardRankProgression } from '../dashboardRankProgressionApi';
import RankHistoryTimeline from '../components/RankHistoryTimeline';
import {
  getCurrentPromotionSource,
  isContractorRank,
} from '../dashboardUtils';
import {
  dashboardCardCacheKey,
  getDashboardCardBoot,
  setDashboardCardCache,
} from '../dashboardSessionCache';

const CARD_ID = 'rankHistory';

export default function RankHistoryCard({
  handleId,
  player,
  loading: profileLoading,
  viewerCacheKey = '',
}) {
  const hid = Number(handleId);
  const hidOk = Number.isInteger(hid) && hid >= 1;
  const cacheKey = hidOk ? dashboardCardCacheKey(viewerCacheKey, hid, CARD_ID) : null;
  const sessionBoot = cacheKey ? getDashboardCardBoot(cacheKey) : null;

  const [promotions, setPromotions] = useState(() => sessionBoot?.promotions ?? []);
  const [rankProgression, setRankProgression] = useState(
    () => sessionBoot?.rankProgression ?? null
  );
  const [loading, setLoading] = useState(() => hidOk && !sessionBoot);
  const [error, setError] = useState(sessionBoot?.error ?? null);
  const hasLoadedOnceRef = useRef(!!sessionBoot);

  useEffect(() => {
    if (!hidOk || profileLoading) {
      return undefined;
    }

    let cancelled = false;

    async function load() {
      const hasCache = !!getDashboardCardBoot(cacheKey);
      const isBlankVisit = !hasLoadedOnceRef.current && !hasCache;

      if (isBlankVisit) {
        setLoading(true);
      }
      if (!isBlankVisit) {
        setError(null);
      }

      try {
        const data = await fetchDashboardRankHistory(hid);
        if (cancelled) return;
        if (!data?.ok) {
          setPromotions([]);
          setError(data?.error || 'Could not load rank history.');
          return;
        }

        const rows = Array.isArray(data.promotions) ? data.promotions : [];
        setPromotions(rows);

        let nextProgression = null;
        const currentSource = getCurrentPromotionSource(rows);
        const contractor = isContractorRank(player?.rankName);
        if (currentSource === 'auto' && !contractor) {
          const progression = await fetchDashboardRankProgression(hid);
          if (cancelled) return;
          if (progression?.ok && progression.nextRank) {
            nextProgression = progression;
            setRankProgression(progression);
          } else {
            setRankProgression(null);
          }
        } else {
          setRankProgression(null);
        }

        hasLoadedOnceRef.current = true;
        setDashboardCardCache(cacheKey, {
          promotions: rows,
          rankProgression: nextProgression,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setPromotions([]);
        setRankProgression(null);
        setError(e?.message || 'Could not load rank history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, hid, hidOk, profileLoading, player?.rankName]);

  if (!hidOk) return null;

  const busy = (profileLoading && !player) || (loading && !promotions.length);
  const currentSource = getCurrentPromotionSource(promotions);
  const showContractorNotice =
    currentSource !== 'override' && isContractorRank(player?.rankName);

  return (
    <section
      className="member-dashboard__panel-card member-dashboard__rank-history-card"
      aria-busy={busy ? 'true' : undefined}
      aria-label="Rank progression"
      data-resource="private.navbar.personalhub.rankHistory"
      data-action="read"
      data-description="Personal hub — rank progression history"
    >
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.rankHistory"
        data-action="read"
        data-route="/api/user/private-dashboard/rank-history"
        data-description="Personal hub — rank history API"
      />
      <div
        className="display-none"
        data-resource="private.navbar.personalhub.rankHistory"
        data-action="read"
        data-route="/api/user/private-dashboard/rank-progression"
        data-description="Personal hub — rank progression API"
      />

      <div className="member-dashboard__panel-card-head">
        <h2 className="member-dashboard__panel-title">Rank progression</h2>
      </div>

      <div className="member-dashboard__panel-card-body">
        {busy ? (
          <p className="member-dashboard__panel-placeholder">Loading…</p>
        ) : error ? (
          <p className="member-dashboard__panel-placeholder">{error}</p>
        ) : (
          <RankHistoryTimeline
            promotions={promotions}
            nextRank={rankProgression?.nextRank ?? null}
            progression={rankProgression?.progression ?? null}
            tokensRedacted={Boolean(rankProgression?.currentRank?.tokensRedactedOverride)}
          />
        )}

        {showContractorNotice ? (
          <p className="member-dashboard__rank-history-footnote">
            Affiliated members are assigned &quot;Contractor&quot; and are not eligible
            for promotion.
          </p>
        ) : null}
      </div>
    </section>
  );
}
